"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import { Fragment } from "react";
import { CTAButton } from "@/shadcn/ui/button";

export default function PayoutsSection({
  data,
  className,
}: {
  data: PageAffiliateSchema;
  className?: string;
}) {
  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block",
              index == 3 && "lg:h-[50%] lg:self-end",
            )}
          />
        ))}
      </div>

      <div className="container flex flex-col items-center pt-[8rem] lg:pt-[15.25rem] pb-[7rem] lg:pb-[20rem] relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <ChipSerif text={data.payoutsChip} />
        </motion.div>

        {data?.payoutsText && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext w-[min(95rem,100%)]"
          >
            <PortableText
              value={data.payoutsText}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {data?.payoutsCta && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mt-[3rem]"
          >
            <CTAButton data={data.payoutsCta} className="" />
          </motion.div>
        )}

        {data?.payoutsItems && data.payoutsItems.length > 0 && (
          <div className="mt-[2.5rem] lg:mt-[4.75rem] w-[min(151rem,100%)] flex flex-col gap-[3.5rem] lg:gap-[4.5rem] mb-[5rem] lg:mb-[8rem]">
            {data.payoutsItems.map((item, index) => (
              <motion.div
                key={index}
                className="flex flex-col justify-between items-center w-full lg:flex-row bg-white border border-[#E5E5E5] p-[3rem]"
                initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
                whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.1,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
              >
                <div className="w-[min(59.625rem,100%)] lg:px-[4.5rem] lg:py-[5rem]">
                  {/* Chip */}

                  {item.chip.text && (
                    <ChipSerif text={item.chip.text} className="mb-[3rem]" />
                  )}
                  {/* Rich Text Content */}
                  {item.text && (
                    <div className="mb-[3rem] lg:mb-[7rem] richtext richtext-start prose-p:text-[#103037] prose-p:font-medium prose-p:text-[3rem]">
                      <PortableText
                        value={item.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                  {/* Name */}
                  {item.name && (
                    <p className="text-[#313131]/60 text-[2rem] leading-[130%] mb-0">
                      {item.name}
                    </p>
                  )}
                </div>

                {/* Image */}

                {item.image && (
                  <div className="w-[min(68.625rem,100%)] overflow-hidden relative rounded-[2rem] mt-[6rem] lg:mt-0">
                    <Image
                      src={item.image}
                      alt={item.name || "payout"}
                      className="object-cover rounded-[2rem]"
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
